import { IconButton, IIconProps, Label, MessageBarType, PrimaryButton, SearchBox, Separator, Spinner, Text } from '@fluentui/react';
import { useEffect, useState } from 'react';
import { useBoolean } from '@fluentui/react-hooks';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ConfirmationButtonComponent } from '../../../commons/component/ConfirmationButton/ConfirmationButtonComponent';
import { MessageComponent } from '../../../commons/component/MessageComponent';
import { SpecialtyViewModel } from '../../../models/Entities/Specialties/SpecialtyModels';
import * as ComponentFunctions from './MedicalSpecialties.Function';
import './MedicalSpecialtiesListComponent.css';
import MedicalSpecialtiesFormComponent from '../MedicalSpecialties.Form/MedicalSpecialtiesForm.Component';

const MedicalSpecialtiesListComponent = (props: ComponentFunctions.Props) => {
  const EditIcon: IIconProps = { iconName: 'Edit' };
  const DeleteIcon: IIconProps = { iconName: 'Delete' };
  const [t] = useTranslation();
  const [isFormOpen, { setTrue: openForm, setFalse: dismissForm }] = useBoolean(false);
  const [selectedSpecialtyId, setSelectedSpecialtyId] = useState<number | undefined>(undefined);
  const [searchText, setSearchText] = useState<string>('');

  useEffect(() => {
    props.GetAllSpecialties();
  }, []);

  const normalizedSearchText = searchText.trim().toLowerCase();

  const filtered = props.specialties.filter((item: SpecialtyViewModel) => {
    if (normalizedSearchText === '') {
      return true;
    }

    const specialtyName = item.name?.toLowerCase() || '';
    const snomedCode = item.snomedCode?.toLowerCase() || '';
    const snomedName = item.snomedName?.toLowerCase() || '';

    return (
      specialtyName.includes(normalizedSearchText)
      || snomedCode.includes(normalizedSearchText)
      || snomedName.includes(normalizedSearchText)
    );
  });

  const handleOpenCreate = () => {
    setSelectedSpecialtyId(undefined);
    openForm();
  };

  const handleEdit = (id: number) => {
    setSelectedSpecialtyId(id);
    openForm();
  };

  const handleDismissForm = () => {
    setSelectedSpecialtyId(undefined);
    dismissForm();
  };

  const onSearch = (newValue?: string) => {
    setSearchText(newValue || '');
  };

  const Searchbox = <SearchBox
    placeholder={t('medicalSpecialties.list.searchPlaceholder', { defaultValue: 'Buscar especialidad' })}
    underlined={false}
    onChange={(
      event?: React.ChangeEvent<HTMLInputElement> | undefined,
      newValue?: string | undefined
    ) => onSearch(newValue || '')}
    onClear={() => onSearch('')}
  />;

  const Controls = (
    <>
      <div className="Controls">
        <div className="AddButton">
          <PrimaryButton
            text={t('medicalSpecialties.list.add', { defaultValue: 'Agregar especialidad' })}
            iconProps={{ iconName: 'Add' }}
            onClick={handleOpenCreate}
          />
        </div>
        {Searchbox}
      </div>
      <Separator />
    </>
  );

  return (
    <>
      {Controls}

      {!props.isLoading && filtered.length === 0 && (
        <MessageComponent
          message={t('medicalSpecialties.list.empty', { defaultValue: 'No hay especialidades registradas' })}
          type={MessageBarType.info}
        />
      )}

      {props.isLoadingSuccessfully && filtered.length > 0 && (
        <table className="Table">
          <thead>
            <tr>
              <th>{t('medicalSpecialties.list.id', { defaultValue: 'ID' })}</th>
              <th>{t('medicalSpecialties.list.name', { defaultValue: 'Especialidad' })}</th>
              <th>{t('medicalSpecialties.list.snomed', { defaultValue: 'SNOMED' })}</th>
              <th>{t('medicalSpecialties.list.snomedName', { defaultValue: 'Nombre SNOMED' })}</th>
              <th style={{ width: '140px' }}>{t('medicalSpecialties.list.actions', { defaultValue: 'Acciones' })}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((item: SpecialtyViewModel) => (
              <tr key={item.id} className="TableBodyRow">
                <td className="primary">{item.id}</td>
                <td>
                  <Label className="upper">{item.name || '-'}</Label>
                </td>
                <td>
                  <Text>{item.snomedCode || '-'}</Text>
                </td>
                <td>
                  <Text>{item.snomedName || '-'}</Text>
                </td>
                <td style={{ width: '140px' }}>
                  <IconButton
                    iconProps={EditIcon}
                    title={t('medicalSpecialties.list.edit', { defaultValue: 'Editar' })}
                    ariaLabel={t('medicalSpecialties.list.edit', { defaultValue: 'Editar' })}
                    onClick={() => handleEdit(item.id)}
                  />
                  <div className='danger'>
                    <ConfirmationButtonComponent
                      ButtonId={`SpecialtyDeleteButton-${item.id}`}
                      ButtonIconName={'Delete'}
                      ButtonText={''}
                      ButtonCssClass={''}
                      ConfirmationButtonText={t('medicalSpecialties.list.delete', { defaultValue: 'Eliminar' })}
                      ConfirmationButtonCssClass={'deleteButton'}
                      CancelButtonText={t('medicalSpecialties.list.cancel', { defaultValue: 'Cancelar' })}
                      CancelButtonCssClass={''}
                      Title={t('medicalSpecialties.list.deleteTitle', { defaultValue: 'Eliminar especialidad' })}
                      Description={t('medicalSpecialties.list.deleteDescription', { defaultValue: '¿Desea eliminar la especialidad?' })}
                      ElementTextHeader={t('medicalSpecialties.list.specialty', { defaultValue: 'Especialidad' })}
                      ElementTextInformation={item.name || ''}
                      onConfirmation={() => props.DeleteSpecialty(item.id)}
                      onCancel={() => undefined}
                      isOperationExecuting={props.isDeleting || false}
                      isOperationExecutedWithError={props.failOnDeleting || false}
                      OperationError={props.error}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {props.isLoading && (
        <div>
          <Spinner
            label={t('medicalSpecialties.list.loading', { defaultValue: 'Cargando especialidades...' })}
            ariaLive="assertive"
            labelPosition="top"
          />
        </div>
      )}

      <MedicalSpecialtiesFormComponent
        isOpen={isFormOpen}
        openPanel={openForm}
        dismissPanel={handleDismissForm}
        specialtyId={selectedSpecialtyId}
      />
    </>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(MedicalSpecialtiesListComponent as any);
